/**
 * Accomplishment moments — the fleet primitive, copied from ptc-statetax
 * (docs/moments-primitive.md there). Supervision's two moments: a review completed, and a trimester
 * closed for a client's books.
 *
 * The six rules, each enforced somewhere in this folder:
 *   1. Verified completion only — the SERVER proves the fact (`GET /my-moments`); the UI never decides one.
 *   2. Personal before comparative — no counts of anyone else, no rankings.
 *   3. Not a modal — a banner in a polite live region; it never takes focus or blocks input.
 *   4. Once — at most once per person per fact id (`seen` in the person's settings).
 *   5. An off switch, per person (`MomentsToggle`), honoured everywhere (`useMoment`).
 *   6. prefers-reduced-motion — words only, no confetti element at all (`MomentHost`).
 */
import { createContext, useEffect, useState } from 'react';

export type MomentKind = 'review-completed' | 'trimester-closed';

export interface Moment {
  /** Stable id of the FACT, e.g. "trimester-closed:BK-1:2026-T3". Seen-once is keyed on it. */
  id: string;
  kind: MomentKind;
  title: string;
  /** Only PROVEN figures. A figure that is not proven is left out, never estimated. */
  detail?: string;
  /** false for quiet moments. Never rendered under prefers-reduced-motion. */
  confetti: boolean;
}

export interface MomentHostApi {
  show: (moment: Moment) => void;
}

/** No host mounted (a component test) ⇒ moments are a silent no-op. */
export const MomentContext = createContext<MomentHostApi>({ show: () => undefined });

/** How long the banner stays up, unless hovered or focused. The confetti itself is under 2 s. */
export const MOMENT_VISIBLE_MS = 4_000;

/** Mirrors the server's cap (`ReviewAccomplishments.MaxSeen`). */
export const MAX_SEEN_MOMENTS = 100;

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/** True when the person asked the OS for reduced motion — moments then show words only. */
export function usePrefersReducedMotion(): boolean {
  const query =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(REDUCED_MOTION) : null;
  const [reduced, setReduced] = useState(() => query?.matches ?? false);
  useEffect(() => {
    if (!query) return;
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, [query]);
  return reduced;
}

// Browser storage is a FALLBACK cache only — it stops a re-fire in this browser when the settings
// write fails. The record is the person's settings document on the server.
const LOCAL_SEEN_KEY = 'supervision.moments.seen';

export function seenLocally(id: string): boolean {
  try {
    const raw = window.localStorage.getItem(LOCAL_SEEN_KEY);
    const list: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(list) && list.includes(id);
  } catch {
    return false;
  }
}

export function markSeenLocally(ids: readonly string[]): void {
  try {
    const raw = window.localStorage.getItem(LOCAL_SEEN_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string') : [];
    for (const id of ids) if (!list.includes(id)) list.push(id);
    window.localStorage.setItem(LOCAL_SEEN_KEY, JSON.stringify(list.slice(-MAX_SEEN_MOMENTS)));
  } catch {
    // Storage blocked (private window, quota) — the server record still holds.
  }
}
